import { useEffect, useMemo, useState } from "react";
import { FounderOperationalShell } from "../../../components/founder/FounderOperationalShell";
import {
  FounderErrorState,
  FounderLoadingState,
} from "../../../components/founder/FounderWorkspaceChrome";
import {
  fetchFounderTrafficSummary,
  type FounderTrafficSummary,
} from "../../../founder/founderWorkspaceApi";

function formatPathLabel(path: string): string {
  const labels: Record<string, string> = {
    "/": "Home",
    "/discover-apps": "Discover Apps",
    "/categories": "Categories",
    "/membership": "Membership",
    "/publish-your-app": "Publish Your App",
    "/app-installation-guide": "App Installation Guide",
    "/app-installation-guide/steps": "Installation Guide Steps",
    "/contact": "Contact",
    "/privacy-policies": "Privacy Policies",
    "/sign-in": "Sign In",
    "/log-in": "Log In",
  };
  return labels[path] ?? path;
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function trendLabel(points: FounderTrafficSummary["trend7Days"]): string {
  const human = points.filter((point) => point.visitors > 0 || point.pageViews > 0);
  if (human.length < 2) {
    return "Not enough recorded days yet to describe a trend.";
  }
  const first = human[0].visitors;
  const last = human[human.length - 1].visitors;
  if (last > first) {
    return "Increasing";
  }
  if (last < first) {
    return "Declining";
  }
  return "Stable";
}

function BreakdownList({
  title,
  rows,
  empty,
}: {
  title: string;
  rows: Array<{ name: string; count: number }>;
  empty: string;
}) {
  return (
    <section className="oac-founder-ops-panel">
      <h2 className="oac-founder-ops-panel__title">{title}</h2>
      {rows.length === 0 ? (
        <p className="oac-founder-ops-panel__empty">{empty}</p>
      ) : (
        <ul className="oac-founder-ops-list oac-founder-ops-list--compact">
          {rows.map((row) => (
            <li className="oac-founder-ops-list__item" key={row.name}>
              <span className="oac-founder-ops-list__title">{row.name}</span>
              <span className="oac-founder-ops-list__meta">{formatNumber(row.count)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function FounderTrafficWorkspace() {
  const [traffic, setTraffic] = useState<FounderTrafficSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchFounderTrafficSummary()
      .then((data) => {
        if (!cancelled) {
          setTraffic(data);
          setError("");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Traffic recording is not available yet.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const metrics = useMemo(() => {
    if (!traffic) {
      return [];
    }
    return [
      { id: "today", label: "Visitors today", value: traffic.visitorsToday, tone: "mint" as const },
      { id: "d7", label: "Visitors — 7 days", value: traffic.visitorsLast7Days, tone: "teal" as const },
      { id: "d30", label: "Visitors — 30 days", value: traffic.visitorsLast30Days, tone: "steel" as const },
      { id: "total", label: "Total recorded visitors", value: traffic.totalRecordedVisitors, tone: "blue" as const },
    ];
  }, [traffic]);

  return (
    <FounderOperationalShell metrics={metrics} title="Website Traffic">
      {loading ? <FounderLoadingState label="Loading traffic..." /> : null}
      {!loading && error ? <FounderErrorState message={error} /> : null}

      {!loading && !error && traffic ? (
        <div className="oac-founder-ops-grid oac-founder-ops-grid--2col">
          <section className="oac-founder-ops-panel oac-founder-ops-panel--wide">
            <h2 className="oac-founder-ops-panel__title">Page views</h2>
            <ul className="oac-founder-kv">
              <li>
                <span>Today</span>
                <strong>{formatNumber(traffic.pageViewsToday)}</strong>
              </li>
              <li>
                <span>Last 7 days</span>
                <strong>{formatNumber(traffic.pageViewsLast7Days)}</strong>
              </li>
              <li>
                <span>Last 30 days</span>
                <strong>{formatNumber(traffic.pageViewsLast30Days)}</strong>
              </li>
              <li>
                <span>Total recorded</span>
                <strong>{formatNumber(traffic.totalRecordedPageViews)}</strong>
              </li>
            </ul>
            <p className="oac-founder-ops-panel__lead">
              TRAFFIC RECORDING AVAILABLE FROM:{" "}
              {traffic.recordingStartedAt
                ? new Date(traffic.recordingStartedAt).toLocaleString()
                : "Not recorded"}
            </p>
            <p className="oac-founder-ops-panel__meta">
              Unique visitor: {traffic.uniqueVisitorDefinition}
            </p>
            <p className="oac-founder-ops-panel__meta">
              7-day trend: {trendLabel(traffic.trend7Days)}. 30-day trend:{" "}
              {trendLabel(traffic.trend30Days)}.
            </p>
            <p className="oac-founder-ops-panel__meta">
              Automated traffic (30 days, excluded from headline visitors):{" "}
              {formatNumber(traffic.botPageViewsLast30Days)}
            </p>
          </section>

          <section className="oac-founder-ops-panel">
            <h2 className="oac-founder-ops-panel__title">Top pages</h2>
            {traffic.topPages.length === 0 ? (
              <p className="oac-founder-ops-panel__empty">No page views recorded yet.</p>
            ) : (
              <ul className="oac-founder-ops-list oac-founder-ops-list--compact">
                {traffic.topPages.map((row) => (
                  <li className="oac-founder-ops-list__item" key={row.path}>
                    <div>
                      <span className="oac-founder-ops-list__title">{formatPathLabel(row.path)}</span>
                      <span className="oac-founder-ops-list__meta">{row.path}</span>
                    </div>
                    <span className="oac-founder-ops-list__meta">{formatNumber(row.views)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <BreakdownList
            empty="Not recorded yet."
            rows={traffic.devices}
            title="Device"
          />
          <BreakdownList
            empty="Not recorded yet."
            rows={traffic.browsers}
            title="Browser"
          />
          <BreakdownList
            empty="Not recorded yet."
            rows={traffic.platforms}
            title="Platform"
          />
          <BreakdownList
            empty="Country is not recorded unless a privacy-safe country header is already present. Individual IP addresses are never shown."
            rows={traffic.countries}
            title="Country"
          />
        </div>
      ) : null}
    </FounderOperationalShell>
  );
}
